import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { triggerConfetti } from '@/components/gamification/ConfettiCelebration';
import { toast } from 'sonner';
import { BADGES, Badge } from '@/components/gamification/BadgeCard';
import { BADGE_POINTS } from '@/components/gamification/BadgeGrid';

interface UserStreak {
  currentStreak: number;
  longestStreak: number;
  lastVisit: string;
}

interface UserStats {
  placesVisited: number;
  reviewsCount: number;
  sharesCount: number;
  favoritesCount: number;
}

interface UnlockedBadge {
  badgeId: string;
  unlockedAt: string;
}

export const useGamification = () => {
  const { user } = useAuth();
  const [streak, setStreak] = useState<UserStreak | null>(null);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [badges, setBadges] = useState<UnlockedBadge[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch user gamification data
  const fetchGamificationData = useCallback(async () => {
    if (!user) {
      setStreak(null);
      setStats(null);
      setBadges([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      // Fetch streak
      const { data: streakData } = await supabase
        .from('user_streaks')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (streakData) {
        setStreak({
          currentStreak: streakData.current_streak,
          longestStreak: streakData.longest_streak,
          lastVisit: streakData.last_visit,
        });
      }

      // Fetch stats
      const { data: statsData } = await supabase
        .from('user_stats')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (statsData) {
        setStats({
          placesVisited: statsData.places_visited,
          reviewsCount: statsData.reviews_count,
          sharesCount: statsData.shares_count,
          favoritesCount: statsData.favorites_count,
        });
      }

      // Fetch badges
      const { data: badgesData } = await supabase
        .from('user_badges')
        .select('badge_id, unlocked_at')
        .eq('user_id', user.id);

      if (badgesData) {
        setBadges(badgesData.map(b => ({
          badgeId: b.badge_id,
          unlockedAt: b.unlocked_at,
        })));
      }
    } catch (error) {
      console.error('Error fetching gamification data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Update streak on app visit
  const updateStreak = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase.rpc('update_user_streak', {
        p_user_id: user.id,
      });

      if (error) throw error;

      if (data && data.length > 0) {
        const result = data[0];
        const newStreak = {
          currentStreak: result.current_streak,
          longestStreak: result.longest_streak,
          lastVisit: new Date().toISOString().split('T')[0],
        };
        
        setStreak(newStreak);

        // Celebrate streak milestones
        if (result.streak_increased) {
          if (result.current_streak === 7) {
            toast.success('🔥 Streak de 7 jours ! Badge "Fidèle" débloqué !');
            triggerConfetti('badge');
            await unlockBadge('faithful');
          } else if (result.current_streak === 30) {
            toast.success('🌟 30 jours consécutifs ! Incroyable !');
            triggerConfetti('milestone');
          } else if (result.current_streak === 100) {
            toast.success('🏆 100 jours ! Vous êtes légendaire !');
            triggerConfetti('milestone');
          }
        }
      }
    } catch (error) {
      console.error('Error updating streak:', error);
    }
  }, [user]);

  // Unlock a badge and award points
  const unlockBadge = useCallback(async (badgeId: string) => {
    if (!user) return false;

    // Check if already unlocked
    if (badges.some(b => b.badgeId === badgeId)) return false;

    try {
      const { error } = await supabase
        .from('user_badges')
        .insert({ user_id: user.id, badge_id: badgeId });

      if (error) {
        if (error.code === '23505') return false; // Already exists
        throw error;
      }

      const badge = BADGES.find(b => b.id === badgeId);
      const points = BADGE_POINTS[badgeId] || 0;

      // Award points for the badge
      if (points > 0) {
        await supabase.rpc('add_user_points', {
          p_user_id: user.id,
          p_amount: points,
          p_source: `badge_${badgeId}`,
          p_description: `Badge ${badge?.name || badgeId} débloqué`,
        });
      }

      if (badge) {
        toast.success(`🏆 Badge débloqué : ${badge.name}`, {
          description: `+${points} points gagnés !`,
        });
        triggerConfetti('badge');
      }

      setBadges(prev => [...prev, { badgeId, unlockedAt: new Date().toISOString() }]);
      return true;
    } catch (error) {
      console.error('Error unlocking badge:', error);
      return false;
    }
  }, [user, badges]);

  // Increment a stat and check for badge unlocks
  const incrementStat = useCallback(async (
    statName: 'places_visited' | 'reviews_count' | 'shares_count' | 'favorites_count',
    increment: number = 1
  ) => {
    if (!user) return;

    try {
      const { data: newValue } = await supabase.rpc('increment_user_stat', {
        p_user_id: user.id,
        p_stat_name: statName,
        p_increment: increment,
      });

      // Update local state
      setStats(prev => {
        if (!prev) {
          return {
            placesVisited: statName === 'places_visited' ? increment : 0,
            reviewsCount: statName === 'reviews_count' ? increment : 0,
            sharesCount: statName === 'shares_count' ? increment : 0,
            favoritesCount: statName === 'favorites_count' ? increment : 0,
          };
        }
        
        return {
          ...prev,
          placesVisited: statName === 'places_visited' ? (newValue || prev.placesVisited + increment) : prev.placesVisited,
          reviewsCount: statName === 'reviews_count' ? (newValue || prev.reviewsCount + increment) : prev.reviewsCount,
          sharesCount: statName === 'shares_count' ? (newValue || prev.sharesCount + increment) : prev.sharesCount,
          favoritesCount: statName === 'favorites_count' ? (newValue || prev.favoritesCount + increment) : prev.favoritesCount,
        };
      });

      // Check for badge unlocks based on the new value
      if (newValue) {
        if (statName === 'places_visited' && newValue >= 10) {
          await unlockBadge('explorer');
        }
        if (statName === 'reviews_count' && newValue >= 3) {
          await unlockBadge('critic');
        }
        if (statName === 'shares_count' && newValue >= 5) {
          await unlockBadge('ambassador');
        }
      }
    } catch (error) {
      console.error('Error incrementing stat:', error);
    }
  }, [user, unlockBadge]);

  // Get badge progress
  const getBadgeProgress = useCallback((badgeId: string): number => {
    if (!stats && !streak) return 0;

    switch (badgeId) {
      case 'explorer':
        return stats?.placesVisited || 0;
      case 'gourmet':
        return 0; // Would need category tracking
      case 'faithful':
        return streak?.currentStreak || 0;
      case 'contributor':
        return 0; // Would need edit request tracking
      case 'ambassador':
        return stats?.sharesCount || 0;
      case 'critic':
        return stats?.reviewsCount || 0;
      case 'pioneer':
        return 0; // Would need place creation tracking
      case 'globetrotter':
        return 0; // Would need city tracking
      default:
        return 0;
    }
  }, [stats, streak]);

  // Check if badge is unlocked
  const isBadgeUnlocked = useCallback((badgeId: string): boolean => {
    return badges.some(b => b.badgeId === badgeId);
  }, [badges]);

  // Get unlocked badge data
  const getUnlockedBadge = useCallback((badgeId: string): UnlockedBadge | undefined => {
    return badges.find(b => b.badgeId === badgeId);
  }, [badges]);

  // Initial load and streak update
  useEffect(() => {
    fetchGamificationData();
  }, [fetchGamificationData]);

  useEffect(() => {
    if (user) {
      updateStreak();
    }
  }, [user, updateStreak]);

  return {
    streak,
    stats,
    badges,
    isLoading,
    updateStreak,
    incrementStat,
    unlockBadge,
    getBadgeProgress,
    isBadgeUnlocked,
    getUnlockedBadge,
    refetch: fetchGamificationData,
  };
};
